import {
  Badge,
  Button,
  ButtonGroup,
  Flex,
  IconButton,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { RiMore2Fill } from "@remixicon/react";
import { useQueries } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { useAdminDemoAppDeleteMutation } from "@/admin/data/useAdminDemoAppDeleteMutation";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import api from "@/common/utils/api";
import { AppDTO } from "@reflag/shared/appAPI";
import { EnvironmentDTO } from "@reflag/shared/environmentAPI";
import { OrgHeader } from "@reflag/shared/organizationAPI";
import { EnvironmentUrl } from "@reflag/shared/urls";

export default function AdminOrgApps({ org }: { org: OrgHeader }) {
  const appQueries = useQueries({
    queries: org.apps.map((app) => ({
      queryKey: ["apps", app.id, "details"],
      queryFn: () =>
        api.get<"/apps/:appId">(`/apps/${app.id}`).then((res) => res.data),
    })),
  });

  const apps = useMemo(
    () =>
      org.apps.map((app, index) => ({
        header: app,
        details: appQueries[index]?.data,
        isLoading: appQueries[index]?.isLoading ?? true,
        isError: appQueries[index]?.isError ?? false,
      })),
    [appQueries, org.apps],
  );

  return (
    <Table>
      <Thead>
        <Tr>
          <Th>App</Th>
          <Th>Publishable key</Th>
          <Th></Th>
        </Tr>
      </Thead>
      <Tbody>
        {apps.map(({ header, details, isLoading, isError }) => (
          <AppRow
            key={header.id}
            appId={header.id}
            app={details}
            appName={header.name}
            demo={header.demo}
            env={details?.environments.find((env) => env.isProduction) ?? null}
            isError={isError}
            isLoading={isLoading}
            org={org}
          />
        ))}
      </Tbody>
    </Table>
  );
}

function AppRow({
  org,
  appId,
  app,
  appName,
  demo,
  env,
  isError,
  isLoading,
}: {
  org: OrgHeader;
  appId: string;
  app: AppDTO | undefined;
  appName: string;
  demo: boolean;
  env: EnvironmentDTO | null;
  isError: boolean;
  isLoading: boolean;
}) {
  const { setActiveOrg } = useEnvironment();
  const appDeleteMutation = useAdminDemoAppDeleteMutation(org.id, appId);
  const [demoAppUserId, setDemoAppUserId] = useState(crypto.randomUUID());

  const demoAppUrl = useMemo(() => {
    const url = new URL("https://live-satisfaction-demo.vercel.app/");
    if (env) {
      url.searchParams.set("publishableKey", env.sdkAccess.publishableKey);
      url.searchParams.set("userId", demoAppUserId);
    }
    return url;
  }, [demoAppUserId, env]);

  return (
    <Tr key={app?.id ?? appName}>
      <Td>
        <Flex alignItems="center">
          {env ? (
            <Link
              href={EnvironmentUrl(env)}
              onClick={() => {
                setActiveOrg(org.id, { refresh: false });
              }}
            >
              {appName}
            </Link>
          ) : (
            <span>{appName}</span>
          )}
          {demo ? <Badge mx="2">Demo</Badge> : null}
        </Flex>
      </Td>
      <Td>{env ? env.sdkAccess.publishableKey : "Loading..."}</Td>
      <Td textAlign="right">
        <ButtonGroup>
          <Button
            colorScheme="red"
            isDisabled={!demo || isLoading || isError}
            size="sm"
            title={
              !demo
                ? "Only demo apps can be deleted by admins"
                : isError
                  ? "Unable to load app details"
                  : undefined
            }
            onClick={() => appDeleteMutation.mutate()}
          >
            Delete
          </Button>
          <Menu placement="bottom-end">
            <MenuButton
              aria-label="More options"
              as={IconButton}
              color="dimmed"
              icon={<RiMore2Fill size={16} />}
              size="sm"
              variant="ghost"
            />
            <MenuList>
              <MenuItem
                as={Link}
                href={demoAppUrl.toString()}
                target="_blank"
                isDisabled={!env}
                onClick={() => setDemoAppUserId(crypto.randomUUID())}
              >
                Open Slick demo app
              </MenuItem>
            </MenuList>
          </Menu>
        </ButtonGroup>
      </Td>
    </Tr>
  );
}
